import * as React from 'react';
import { Check, ChevronDown, ChevronRight, FileText, Folder, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { MOVABLE_DOCUMENT_CATEGORIES } from '@hector/shared-types';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAllCases } from '@/hooks/useCases';
import { useFolderContents } from '@/hooks/useFolderContents';
import { getCategoryTabLabel } from '@/lib/droit-public';
import { cn } from '@/lib/utils';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_SUBCATEGORIES,
  getSubcategoryLabel,
  type DocumentCategoryKey,
} from '@/data/documentCategories';
import { type CaseDocumentRecord } from '@/services/folderHierarchyService';
import { storageService } from '@/services/storageService';
import {
  captureCanvasResidentReplacementTarget,
  replaceCanvasResidentTemplate,
} from '../state/canvasResidentPresentation';
import { isCanvasSessionProtectedByWork } from '../state/canvasSessionProtection';
import {
  detacherLeDocumentCourant,
  preparerLaSortieDuDocumentCourant,
} from '../state/canvasBasculeDeBrouillon';
import {
  decrirePiecePourLeCanvas,
  laPieceSOuvreDansLeCanvas,
  titreDeLaPiece,
  useOuvertureDePiece,
  type MetaDeLaPieceOuverte,
} from '../hooks/useOuvertureDePiece';

const ALL_SUBCATEGORIES = '__all__';

function subcategoryOptions(category: DocumentCategoryKey) {
  return Object.values(DOCUMENT_SUBCATEGORIES).filter((s) => s.parentCategory === category);
}

interface CanvasDocumentOpenDialogProps {
  /** Ouverture contrôlée par le parent (dropdown `CanvasOpenMenu`). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialCaseId?: string | null;
  /**
   * LE PORTEUR — la conversation qui reçoit le document choisi, et donc
   * l'identité du document remplacé (#1336).
   */
  conversationId: string;
}

export function CanvasDocumentOpenDialog({
  open,
  onOpenChange,
  initialCaseId,
  conversationId,
}: CanvasDocumentOpenDialogProps): React.JSX.Element {
  const [selectedCaseId, setSelectedCaseId] = React.useState<string>('');
  const [category, setCategory] = React.useState<DocumentCategoryKey>('procedure');
  const [subcategory, setSubcategory] = React.useState<string>(ALL_SUBCATEGORIES);
  const [path, setPath] = React.useState<string[]>([]);
  const [query, setQuery] = React.useState('');
  const [caseSelectorOpen, setCaseSelectorOpen] = React.useState(false);
  const [loadingDocumentId, setLoadingDocumentId] = React.useState<string | null>(null);

  const { data: cases = [], isLoading: casesLoading } = useAllCases();
  React.useEffect(() => {
    if (selectedCaseId || cases.length === 0) {
      return;
    }
    const initialCase = initialCaseId ? cases.find((caseItem) => caseItem.id === initialCaseId) : undefined;
    setSelectedCaseId(initialCase?.id ?? cases[0]?.id ?? '');
  }, [cases, initialCaseId, selectedCaseId]);

  // Une fois par ouverture : le dossier de la conversation n'est qu'un point de
  // départ. Recalé à chaque changement, le sélecteur revenait aussitôt dessus et
  // aucune pièce d'un autre dossier ne pouvait s'ouvrir (spec #2592).
  const caleSurLeDossierInitial = React.useRef(false);
  React.useEffect(() => {
    if (!open) {
      caleSurLeDossierInitial.current = false;
      return;
    }
    if (caleSurLeDossierInitial.current || !initialCaseId) {
      return;
    }
    if (!cases.some((caseItem) => caseItem.id === initialCaseId)) {
      return;
    }
    caleSurLeDossierInitial.current = true;
    if (selectedCaseId === initialCaseId) {
      return;
    }
    setSelectedCaseId(initialCaseId);
    setPath([]);
    setQuery('');
  }, [cases, initialCaseId, open, selectedCaseId]);

  const effectiveSubcategory = subcategory === ALL_SUBCATEGORIES ? undefined : subcategory;
  const { folders, documents, isLoading } = useFolderContents(
    selectedCaseId,
    category,
    path,
    effectiveSubcategory,
    { enabled: open && selectedCaseId.length > 0 },
  );

  const filteredDocuments = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    return documents
      .filter(laPieceSOuvreDansLeCanvas)
      .filter((doc) => {
        if (!needle) return true;
        return titreDeLaPiece(doc).toLowerCase().includes(needle) || (doc.name ?? '').toLowerCase().includes(needle);
      });
  }, [documents, query]);

  const handleCategoryChange = (value: string) => {
    setCategory(value as DocumentCategoryKey);
    setSubcategory(ALL_SUBCATEGORIES);
    setPath([]);
  };

  const handleSubcategoryChange = (value: string) => {
    setSubcategory(value);
    setPath([]);
  };

  // Les frontières du geste, mémoïsées : elles entrent dans le `useCallback`
  // d'`ouvrir`, qu'un objet recréé à chaque rendu déstabiliserait.
  const dependancesDOuverture = React.useMemo(
    () => ({
      telechargerLeFichier: (chemin: string) => storageService.downloadFile(chemin),
      capturerCibleDuCanvas: async () => captureCanvasResidentReplacementTarget({ conversationId }),
      // Cette dialog vit DANS le canvas : il est déjà déployé et déjà pris par la
      // conversation affichée (`handleUserOpenCanvas`). Elle n'a donc rien à
      // prendre — elle se contente de déclarer le porteur que le canvas a déjà.
      // Ce porteur n'est plus nullable depuis #1336 : le canvas ne s'ouvre que
      // dans un fil confirmé (#1331), et la conversation EST l'identité du
      // document. Le refus qui vivait ici n'avait donc plus de cas à couvrir.
      designerLePorteur: (): string => conversationId,
      poserDansLeCanvas: (target: import('../state/canvasResidentPresentation').CanvasResidentReplacementTarget, blob: Blob, meta: MetaDeLaPieceOuverte) => {
        const replacement = replaceCanvasResidentTemplate({
          target,
          blob,
          meta,
        });
        return replacement.accepted;
      },
      canvasProtectedByWork: (
        target: import('../state/canvasResidentPresentation').CanvasResidentReplacementTarget,
      ) => isCanvasSessionProtectedByWork(target.identity.conversationId),
      // La pièce ouverte devient un document NEUF du fil : le sortant est
      // enregistré d'abord, puis détaché juste après la pose — sans quoi la
      // pièce s'enregistrait par-dessus lui (constat de revue du 2026-09-23).
      preparerLaSortie: (porteur: string) =>
        preparerLaSortieDuDocumentCourant(porteur, { creeUnDocument: true }),
      detacherLeDocumentCourant,
      annoncerLeSucces: (texte: string) => toast.success(texte),
      annoncerLEchec: (texte: string) => toast.error(texte),
    }),
    [conversationId],
  );
  const ouvertureDePiece = useOuvertureDePiece(dependancesDOuverture);

  const handleLoadDocument = async (doc: CaseDocumentRecord) => {
    if (!doc.id) return;
    // La liste n'exige pas de chemin de stockage (`laPieceSOuvreDansLeCanvas` ne
    // juge que le format) : le manque se découvre ici, et se dit.
    const piece = decrirePiecePourLeCanvas(doc, selectedCaseId || null);
    if (!piece) {
      toast.error('Ce document Word n\u2019a pas de chemin de fichier exploitable');
      return;
    }
    // `loadingDocumentId` reste local : `enCours` du hook est un booléen unique,
    // or cette liste doit filer le sablier sur LA ligne cliquée.
    setLoadingDocumentId(doc.id);
    try {
      const resultat = await ouvertureDePiece.ouvrir(piece);
      if (resultat.pose) onOpenChange(false);
    } finally {
      setLoadingDocumentId(null);
    }
  };

  const categories = MOVABLE_DOCUMENT_CATEGORIES;
  const subcategories = subcategoryOptions(category);
  const selectedCase = cases.find((caseItem) => caseItem.id === selectedCaseId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 gap-0 max-h-[85vh] overflow-hidden">
        <DialogHeader className="px-5 py-4 border-b border-grey-200">
          <DialogTitle>Importer un document Word dans le canvas</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-[280px_minmax(0,1fr)] h-[640px] max-h-[calc(85vh-73px)] min-h-0">
          <aside className="min-h-0 border-r border-grey-200 bg-grey-50 p-4 space-y-4 overflow-y-auto">
            <div className="space-y-2">
              <label className="text-xs font-medium text-grey-600">Dossier</label>
              <Popover open={caseSelectorOpen} onOpenChange={setCaseSelectorOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={caseSelectorOpen}
                    className="h-10 w-full justify-between bg-white px-3 font-normal"
                    disabled={casesLoading}
                  >
                    <span className="min-w-0 truncate">
                      {casesLoading ? 'Chargement...' : selectedCase?.title ?? 'Choisir un dossier'}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-grey-500" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[320px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Rechercher un dossier" />
                    <CommandList className="max-h-[320px]">
                      <CommandEmpty>Aucun dossier trouvé</CommandEmpty>
                      <CommandGroup>
                        {cases.map((caseItem) => (
                          <CommandItem
                            key={caseItem.id}
                            value={`${caseItem.title} ${caseItem.id}`}
                            onSelect={() => {
                              setSelectedCaseId(caseItem.id);
                              setPath([]);
                              setQuery('');
                              setCaseSelectorOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4 shrink-0',
                                caseItem.id === selectedCaseId ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            <span className="min-w-0 truncate">{caseItem.title}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-grey-600">Catégorie</label>
              <Select value={category} onValueChange={handleCategoryChange}>
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  {categories.map((key) => (
                    <SelectItem key={key} value={key}>
                      {getCategoryTabLabel(selectedCase, key)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {DOCUMENT_CATEGORIES[category].hasSubcategories && (
              <div className="space-y-2">
                <label className="text-xs font-medium text-grey-600">Sous-catégorie</label>
                <Select value={subcategory} onValueChange={handleSubcategoryChange}>
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[320px]">
                    <SelectItem value={ALL_SUBCATEGORIES}>Toutes</SelectItem>
                    {subcategories.map((item) => (
                      <SelectItem key={item.key} value={item.key}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </aside>

          <section className="min-h-0 min-w-0 flex flex-col">
            <div className="border-b border-grey-200 px-4 py-3 space-y-3">
              <div className="flex min-w-0 items-center gap-1 text-sm text-grey-600">
                <button type="button" className="shrink-0 hover:text-charcoal" onClick={() => setPath([])}>
                  {getSubcategoryLabel(effectiveSubcategory) ?? getCategoryTabLabel(selectedCase, category)}
                </button>
                {path.map((segment, index) => {
                  const targetPath = path.slice(0, index + 1);
                  return (
                    <React.Fragment key={targetPath.join('/')}>
                      <ChevronRight className="h-4 w-4 shrink-0 text-grey-400" />
                      <button
                        type="button"
                        className="min-w-0 truncate hover:text-charcoal"
                        onClick={() => setPath(targetPath)}
                      >
                        {segment}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-grey-400" />
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Rechercher un document Word"
                  className="h-9 pl-9"
                />
              </div>
            </div>

            <ScrollArea className="min-h-0 flex-1">
              <div className="p-4">
                {isLoading ? (
                  <div className="h-64 flex items-center justify-center text-sm text-grey-600">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Chargement
                  </div>
                ) : (
                  <div className="space-y-1">
                    {folders.map((folder) => (
                      <button
                        key={folder.id}
                        type="button"
                        onClick={() => setPath(folder.path)}
                        className="w-full h-10 px-3 rounded-md flex items-center gap-3 text-left text-sm hover:bg-grey-50"
                      >
                        <Folder className="h-4 w-4 text-grey-500" />
                        <span className="flex-1 min-w-0 truncate">{folder.name}</span>
                        <ChevronRight className="h-4 w-4 text-grey-400" />
                      </button>
                    ))}

                    {filteredDocuments.map((doc) => (
                      <button
                        key={doc.id}
                        type="button"
                        disabled={loadingDocumentId !== null}
                        onClick={() => void handleLoadDocument(doc)}
                        className={cn(
                          'w-full h-11 px-3 rounded-md flex items-center gap-3 text-left text-sm hover:bg-grey-50 disabled:opacity-60',
                          loadingDocumentId === doc.id && 'bg-grey-50',
                        )}
                      >
                        {loadingDocumentId === doc.id ? (
                          <Loader2 className="h-4 w-4 text-grey-500 animate-spin" />
                        ) : (
                          <FileText className="h-4 w-4 text-grey-500" />
                        )}
                        <span className="flex-1 min-w-0 truncate">{titreDeLaPiece(doc)}</span>
                        <span className="text-xs text-grey-500">.docx</span>
                      </button>
                    ))}

                    {folders.length === 0 && filteredDocuments.length === 0 && (
                      <div className="h-64 flex items-center justify-center text-sm text-grey-500">
                        Aucun document Word ouvrable ici
                      </div>
                    )}
                  </div>
                )}
              </div>
            </ScrollArea>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
